import { Modal } from './Modal'

export interface ConfirmDialogProps {
  title: string
  message: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Confirmation dialog component
 *
 * Displays a modal dialog asking the user to confirm a destructive action.
 * Typically used for delete confirmations or other irreversible operations.
 * Supports ESC key and overlay click to cancel.
 *
 * @example
 * ```tsx
 * const [showConfirm, setShowConfirm] = useState(false)
 *
 * {showConfirm && (
 *   <ConfirmDialog
 *     title="Delete Template"
 *     message="Are you sure you want to delete this template? This action cannot be undone."
 *     confirmLabel="Delete"
 *     onConfirm={() => {
 *       handleDelete()
 *       setShowConfirm(false)
 *     }}
 *     onCancel={() => setShowConfirm(false)}
 *   />
 * )}
 * ```
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal open={true} onClose={onCancel} size="sm" theme="light">
      <Modal.Body>
        <h3 className="text-lg font-semibold text-secondary mb-2">{title}</h3>
        <p className="text-sm text-grey mb-6">{message}</p>
      </Modal.Body>
      <Modal.Footer>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-grey border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
        >
          {confirmLabel}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
